import { Fragment, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { Collapse } from 'react-bootstrap';
import { getMenuItemsForRole } from '../data/menu-items';
import { useAuth } from '../context/AuthContext';
import orderRepository from '../services/repositories/order.repository';
import '../styles/sidebar.scss';

const MenuItemWithChildren = ({
  item,
  className,
  linkClassName,
  subMenuClassName,
  activeMenuItems,
  toggleMenu,
  isCollapsed = false,
  onItemClick
}) => {
  const [open, setOpen] = useState(activeMenuItems.includes(item.key));
  const [showFlyout, setShowFlyout] = useState(false);
  const [flyoutTop, setFlyoutTop] = useState(0);
  const navItemRef = useRef(null);

  useEffect(() => {
    setOpen(activeMenuItems.includes(item.key));
  }, [activeMenuItems, item]);

  const toggleMenuItem = (e) => {
    e.preventDefault();
    if (isCollapsed) {
      // Calculate position for flyout
      if (navItemRef.current) {
        const rect = navItemRef.current.getBoundingClientRect();
        setFlyoutTop(rect.top);
      }
      // In collapsed mode, toggle flyout
      setShowFlyout(!showFlyout);
    } else {
      // Normal mode, toggle collapse
      const status = !open;
      setOpen(status);
      if (toggleMenu) toggleMenu(item, status);
    }
    return false;
  };

  const getActiveClass = useCallback((item) => {
    return activeMenuItems?.includes(item.key) ? 'active' : '';
  }, [activeMenuItems]);

  // Close flyout when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (showFlyout && !e.target.closest('.nav-item-with-flyout')) {
        setShowFlyout(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [showFlyout]);

  return (
    <li ref={navItemRef} className={`${className} ${isCollapsed ? 'nav-item-with-flyout' : ''}`}>
      <div
        onClick={toggleMenuItem}
        aria-expanded={isCollapsed ? showFlyout : open}
        role="button"
        className={`nav-link menu-arrow ${getActiveClass(item)}`}
      >
        {item.icon && (
          <span className="nav-icon">
            <Icon icon={item.icon} />
          </span>
        )}
        <span className="nav-text">{item.label}</span>
        {!item.badge ? (
          <Icon icon="bx:chevron-down" className="menu-arrow-icon ms-auto" />
        ) : (
          <span className={`badge badge-pill text-end bg-${item.badge.variant}`}>
            {item.badge.text}
          </span>
        )}
      </div>

      {/* Flyout menu for collapsed sidebar */}
      {isCollapsed && showFlyout && (
        <div className="flyout-menu" style={{ top: flyoutTop }}>
          <div className="flyout-header">{item.label}</div>
          <ul className="flyout-nav">
            {(item.children || []).map((child, idx) => (
              <li key={child.key + idx} className="flyout-item">
                <Link
                  to={child.url ?? ''}
                  className={`flyout-link ${getActiveClass(child)}`}
                  onClick={() => {
                    setShowFlyout(false);
                    if (onItemClick) {
                      const overlay = document.querySelector('.sidebar-overlay');
                      if (overlay) onItemClick();
                    }
                  }}
                >
                  {child.icon && <Icon icon={child.icon} className="flyout-icon" />}
                  <span>{child.label}</span>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Normal collapse menu */}
      {!isCollapsed && (
        <Collapse in={open}>
          <div>
            <ul className={subMenuClassName}>
              {(item.children || []).map((child, idx) => (
                <Fragment key={child.key + idx}>
                  {child.children ? (
                    <MenuItemWithChildren
                      item={child}
                      linkClassName={`nav-link ${getActiveClass(child)}`}
                      activeMenuItems={activeMenuItems}
                      className="sub-nav-item"
                      subMenuClassName="nav sub-navbar-nav"
                      toggleMenu={toggleMenu}
                      isCollapsed={isCollapsed}
                      onItemClick={onItemClick}
                    />
                  ) : (
                    <MenuItem
                      item={child}
                      className="sub-nav-item"
                      linkClassName={`sub-nav-link ${getActiveClass(child)}`}
                      onItemClick={onItemClick}
                    />
                  )}
                </Fragment>
              ))}
            </ul>
          </div>
        </Collapse>
      )}
    </li>
  );
};

const MenuItem = ({ item, className, linkClassName, onItemClick }) => {
  return (
    <li className={className}>
      <MenuItemLink item={item} className={linkClassName} onItemClick={onItemClick} />
    </li>
  );
};

const MenuItemLink = ({ item, className, onItemClick }) => {
  const handleClick = () => {
    // Close sidebar on mobile only (when overlay is visible)
    if (onItemClick) {
      const overlay = document.querySelector('.sidebar-overlay');
      if (overlay) {
        onItemClick();
      }
    }
  };

  return (
    <Link
      to={item.url ?? ''}
      target={item.target}
      className={`${className} ${item.isDisabled ? 'disabled' : ''}`}
      onClick={handleClick}
    >
      {item.icon && (
        <span className="nav-icon">
          <Icon icon={item.icon} />
        </span>
      )}
      <span className="nav-text">{item.label}</span>
      {item.badge && (
        <span className={`badge badge-pill text-end bg-${item.badge.variant}`}>
          {item.badge.text}
        </span>
      )}
    </Link>
  );
};

const Sidebar = ({ isOpen, isCollapsed, toggleSidebar }) => {
  const location = useLocation();
  const { user } = useAuth();
  const [activeMenuItems, setActiveMenuItems] = useState([]);
  const [sidebarLogo, setSidebarLogo] = useState(null);
  const [pendingOrdersCount, setPendingOrdersCount] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const fetchPendingOrdersCount = async () => {
      try {
        const response = await orderRepository.getAll();
        const orders = Array.isArray(response) ? response : (response?.data || []);
        const pendingCount = orders.filter((order) => order.status === 'pending').length;
        if (!cancelled) {
          setPendingOrdersCount(pendingCount);
        }
      } catch (error) {
        console.error('Error fetching pending orders count:', error);
        if (!cancelled) {
          setPendingOrdersCount(0);
        }
      }
    };

    fetchPendingOrdersCount();
    const intervalId = setInterval(fetchPendingOrdersCount, 60000);

    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
  }, []);

  // Get logo from settings - first from cache, then fetch from API
  useEffect(() => {
    const getCachedSettings = () => {
      try {
        const cached = localStorage.getItem('siteSettings');
        if (cached) {
          const settings = JSON.parse(cached);
          return settings.header_logo_black || null;
        }
      } catch {
        return null;
      }
      return null;
    };
    
    // Set from cache first
    const cachedLogo = getCachedSettings();
    if (cachedLogo) {
      setSidebarLogo(cachedLogo);
    }
    
    // Fetch fresh settings from API
    const fetchSettings = async () => {
      try {
        const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
        const response = await fetch(`${API_BASE_URL}/public/settings`);
        const data = await response.json();
        if (data.success && data.data.header_logo_black) {
          setSidebarLogo(data.data.header_logo_black);
          // Update cache
          localStorage.setItem('siteSettings', JSON.stringify(data.data));
        }
      } catch (error) {
        console.error('Error fetching sidebar logo:', error);
      }
    };
    fetchSettings();
    
    // Listen for storage changes (when settings are updated)
    const handleStorageChange = () => {
      setSidebarLogo(getCachedSettings());
    };
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // Build full logo URL
  const getLogoUrl = (logo) => {
    if (!logo) return '/assets/images/logo/logo.png';
    if (logo.startsWith('http')) return logo;
    if (logo.startsWith('/storage/')) {
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
      return `${API_BASE_URL.replace('/api', '')}${logo}`;
    }
    return logo;
  };

  const logoUrl = sidebarLogo ? getLogoUrl(sidebarLogo) : '/assets/images/logo/logo.png';

  // Filtrer les menus selon le rôle de l'utilisateur
  const filteredMenuItems = useMemo(() => {
    const userRole = user?.role || 'client';
    const items = getMenuItemsForRole(userRole);

    const applyDynamicBadges = (menuItems) =>
      menuItems.map((item) => {
        const nextItem = { ...item };
        if (item.badge?.dynamic === 'pending-orders') {
          if (pendingOrdersCount === null) {
            nextItem.badge = null;
          } else if (pendingOrdersCount > 0) {
            nextItem.badge = {
              ...item.badge,
              text: String(pendingOrdersCount),
            };
          } else {
            nextItem.badge = null;
          }
        }
        if (item.children) {
          nextItem.children = applyDynamicBadges(item.children);
        }
        return nextItem;
      });

    return applyDynamicBadges(items);
  }, [user?.role, pendingOrdersCount]);

  const findAllParent = (menuItems, menuItem) => {
    let parents = [];
    const parent = findMenuItem(menuItems, menuItem.parentKey);
    if (parent) {
      parents.push(parent.key);
      if (parent.parentKey) {
        parents = [...findAllParent(menuItems, parent), ...parents];
      }
    }
    return parents;
  };

  const findMenuItem = (menuItems, menuItemKey) => {
    if (menuItems && menuItemKey) {
      for (let i = 0; i < menuItems.length; i++) {
        if (menuItems[i].key === menuItemKey) {
          return menuItems[i];
        }
        if (menuItems[i].children) {
          const found = findMenuItem(menuItems[i].children, menuItemKey);
          if (found) return found;
        }
      }
    }
    return null;
  };

  const getMenuItemFromURL = (menuItems, url) => {
    if (menuItems && url) {
      for (let i = 0; i < menuItems.length; i++) {
        if (menuItems[i].url === url) {
          return menuItems[i];
        }
        if (menuItems[i].children) {
          const found = getMenuItemFromURL(menuItems[i].children, url);
          if (found) return found;
        }
      }
    }
    return null;
  };

  const toggleMenu = (menuItem, show) => {
    if (show) {
      setActiveMenuItems([menuItem.key, ...findAllParent(filteredMenuItems, menuItem)]);
    }
  };

  const getActiveClass = useCallback((item) => {
    return activeMenuItems?.includes(item.key) ? 'active' : '';
  }, [activeMenuItems]);

  const activeMenu = useCallback(() => {
    const trimmedURL = location.pathname?.replaceAll('', '');
    const matchingMenuItem = getMenuItemFromURL(filteredMenuItems, trimmedURL);
    if (matchingMenuItem) {
      const activeMt = findMenuItem(filteredMenuItems, matchingMenuItem.key);
      if (activeMt) {
        setActiveMenuItems([activeMt.key, ...findAllParent(filteredMenuItems, activeMt)]);
      }
    }
  }, [location.pathname, filteredMenuItems]);

  useEffect(() => {
    if (filteredMenuItems && filteredMenuItems.length > 0) activeMenu();
  }, [activeMenu, filteredMenuItems]);

  return (
    <>
      {/* Overlay for mobile */}
      {isOpen && (
        <div className="sidebar-overlay" onClick={toggleSidebar}></div>
      )}

      {/* Sidebar */}
      <aside className={`main-nav ${isOpen ? 'open' : ''} ${isCollapsed ? 'collapsed' : ''}`}>
        <div className="logo-box">
          <Link to="/admin/dashboard" className="logo-dark">
            <span className="logo-collapsed-text">1870</span>
            <img
              src={logoUrl}
              width={100}
              height={100}
              className="logo-lg"
              alt="logo"
              onError={(e) => {
                e.target.src = '/assets/images/logo/logo.png';
              }}
            />
          </Link>
        </div>

        <div className="scrollbar" data-simplebar>
          <ul className="navbar-nav" id="navbar-nav">
            {(filteredMenuItems || []).map((item, idx) => (
              <Fragment key={item.key + idx}>
                {item.isTitle ? (
                  <li className={`menu-title ${idx !== 0 ? 'mt-2' : ''}`}>
                    {item.label}
                  </li>
                ) : (
                  <>
                    {item.children ? (
                      <MenuItemWithChildren
                        item={item}
                        toggleMenu={toggleMenu}
                        className="nav-item"
                        linkClassName={`nav-link menu-arrow ${getActiveClass(item)}`}
                        subMenuClassName="nav sub-navbar-nav"
                        activeMenuItems={activeMenuItems}
                        isCollapsed={isCollapsed}
                        onItemClick={toggleSidebar}
                      />
                    ) : (
                      <MenuItem
                        item={item}
                        linkClassName={`nav-link ${getActiveClass(item)}`}
                        className="nav-item"
                        onItemClick={toggleSidebar}
                      />
                    )}
                  </>
                )}
              </Fragment>
            ))}
          </ul>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
